import { Data } from "@core/content/types";
import { configureCreatorContentStore, getCreatorPayload } from "./contentStore";
import {
  extractArtworkInputsFromPayload,
  generateImageTargets,
  type ImageTargetDescriptor,
} from "@webcore/core/targets/target.image.8w";

// Cache for generated image targets (populated lazily)
let generatedImageTargets: ImageTargetDescriptor[] | null = null;
let imageTargetsPromise: Promise<ImageTargetDescriptor[]> | null = null;

export {
  ContentData,
  MicrositeData,
  INITIAL_CONTENT_ID,
  initializeCreatorData,
  getContent,
} from "./contentStore";

const getImageTargetSourcePayload = () => {
  if (typeof window !== "undefined" && (window as any).__ARTWORK_DATA__) {
    return (window as any).__ARTWORK_DATA__;
  }

  return getCreatorPayload();
};

// Set this once per project:
// - Local file: new URL("./creatorData.json", import.meta.url).href
// - URL:        "https://launch.eyejack.io/Collection-... ?j=1"
// - Empty string: no collection linked — falls back to DEFAULT_MICROSITE_DATA below
export const CREATOR_DATA_SOURCE = "https://launch.eyejack.io/Collection-e8c48bde-ea2f-42f8-bdc6-80dc99a5275f/json";

// Manual content for non-creator items (scenes, models, etc.)
// Use targetName to match the 8th Wall target and override creator data (e.g. add custom scale)
export const MANUAL_CONTENT: Data[] = [];

// Explicit display order for the carousel. Artwork ids listed here are ordered
// first (in this sequence); any artworks not listed fall back to their creator order.
export const ARTWORK_ORDER: string[] = [
  "Artwork-a22b1859-83b4-4d9a-866c-dc86de128a45",
  "Artwork-2bfc6237-63ea-4fbf-90f6-89b709139082",
  "Artwork-35d745d5-630e-41eb-b71b-664de51067fd",
  "Artwork-44e33b2d-34b6-40fe-802d-c312c5a7d9a3",
  "Artwork-cad03c7a-db51-4eb6-a08b-9a4c85167a18",
  "Artwork-834e87f2-575a-430c-ab18-16adc7845422",
  "Artwork-3cfb432c-6012-45ae-9d79-2cc09b4e9b9c",
  "Artwork-737cc726-a426-42a9-9534-fc9d4c328b5f",
  "Artwork-bff16374-eeff-4836-bc10-082dd7ea91b6",
  "Artwork-635cfb7e-d0ae-4352-a3c0-3a24dbd57ce8",
];

// Shown on the landing screen when no creator collection is linked.
export const DEFAULT_MICROSITE_DATA = {
  name: "Sneaker Con DOHA",
  description: "Coming soon.",
  constructedCover: "",
};

configureCreatorContentStore({
  creatorDataSource: CREATOR_DATA_SOURCE,
  manualContent: MANUAL_CONTENT,
  defaultMicrositeData: DEFAULT_MICROSITE_DATA,
  artworkOrder: ARTWORK_ORDER,
});

/**
 * Get image target data with client-side image generation (async version).
 * Generates target images in the browser using Canvas API.
 */
export const getImageTargetDataAsync = async (): Promise<ImageTargetDescriptor[]> => {
  // Check if pre-built targets are injected
  if ((window as any).__IMAGE_TARGET_DATA__?.length > 0) {
    return (window as any).__IMAGE_TARGET_DATA__;
  }

  // Check if already generated
  if (generatedImageTargets) {
    return generatedImageTargets;
  }

  // Return existing promise if generation is in progress
  if (imageTargetsPromise) {
    return imageTargetsPromise;
  }

  // Generate client-side using Canvas API
  const artworkPayload = getImageTargetSourcePayload();
  const artworkInputs = extractArtworkInputsFromPayload(artworkPayload);
  imageTargetsPromise = generateImageTargets(artworkInputs).then((targets) => {
    generatedImageTargets = targets;
    return targets;
  });

  return imageTargetsPromise;
};
